<script lang="ts">
  import { onMount } from 'svelte';
  import { Chart, registerables } from 'chart.js';
  import { t } from '$core/i18n';
  import {
    computeForgettingCurves,
    buildYearHeatmap,
    type YearHeatmapWeek
  } from '$features/analytics';
  import { dayKey } from '$features/forecast';
  import {
    listAllRecallItems,
    listMacroSessionsPage,
    listAttemptsByUser
  } from '$db/local/repositories';
  import { listReviewEventsByUser } from '$db/local/repositories/review-events.repo';
  import type { MacroSession, RecallItem, ProblemAttempt, ReviewEvent } from '$domain/types';
  import { EmptyState } from '$ui/primitives/EmptyState';

  Chart.register(...registerables);

  // Chart.js defaults aplicados a nivel de módulo, ANTES de cualquier $effect.
  Chart.defaults.color = '#9ca3af';
  Chart.defaults.borderColor = '#27272a';

  function getThemeColor(varName: string, fallback: string) {
    if (typeof window === 'undefined') return fallback;
    return getComputedStyle(document.documentElement).getPropertyValue(varName).trim() || fallback;
  }

  function hexToRgba(hex: string, alpha: number) {
    if (!hex.startsWith('#')) return hex;
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  Chart.defaults.font.family = "'Inter', sans-serif";
  Chart.defaults.font.size = 9;
  Chart.defaults.animation = false;

  let items = $state<RecallItem[]>([]);
  let sessions = $state<MacroSession[]>([]);
  let attempts = $state<ProblemAttempt[]>([]);
  let reviewEvents = $state<ReviewEvent[]>([]);
  let weeks = $state<YearHeatmapWeek[]>([]);


  let thirtyDayChart = $state.raw<Chart | null>(null);
  let fsrsChart = $state.raw<Chart | null>(null);
  let perfChart = $state.raw<Chart | null>(null);
  let todayPieChart = $state.raw<Chart | null>(null);
  let globalPieChart = $state.raw<Chart | null>(null);

  let thirtyDayCanvas = $state<HTMLCanvasElement>();
  let fsrsCanvas = $state<HTMLCanvasElement>();
  let perfCanvas = $state<HTMLCanvasElement>();
  let todayPieCanvas = $state<HTMLCanvasElement>();
  let globalPieCanvas = $state<HTMLCanvasElement>();

  let perfRange = $state(7);

  let curves = $derived(computeForgettingCurves([0.5, 1, 5, 25, 100, 365], 60, 60));



  const FSRS_PALETTE = ['#ef4444', '#f59e0b', '#e11d48', '#10b981', '#3b82f6', '#8b5cf6'] as const;

  let correctCount = $derived(attempts.filter((a) => a.result === 'correct').length);
  let incorrectCount = $derived(attempts.filter((a) => a.result === 'incorrect').length);
  let unresolvedCount = $derived(attempts.filter((a) => a.result === 'unresolved').length);
  
  let daysLearned = $derived.by(() => {
    const days = new Set(
      sessions
        .filter((s) => s.status === 'finished')
        .map((s) => dayKey(new Date(s.startedAt)))
    );
    if (weeks.length === 0) return { days: 0, total: 0, pct: 0 };
    const totalDays = weeks.reduce((acc, w) => acc + w.days.filter((d) => !d.isFuture).length, 0);
    const n = days.size;
    return {
      days: n,
      total: totalDays,
      pct: totalDays > 0 ? Math.round((n / totalDays) * 100) : 0
    };
  });
  let showRecentStartBanner = $derived(daysLearned.days > 0 && daysLearned.pct < 10);

  let activityRange = $state(30);
  let activityOffset = $state(0);
  
  let todayTimeMs = $derived.by(() => {
    const todayKey = dayKey(new Date());
    return sessions
      .filter((s) => s.status === 'finished' && dayKey(new Date(s.startedAt)) === todayKey)
      .reduce((acc, s) => acc + (s.focusDurationMs || 0), 0);
  });

  let yesterdayTimeMs = $derived.by(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const k = dayKey(yesterday);
    return sessions
      .filter((s) => s.status === 'finished' && dayKey(new Date(s.startedAt)) === k)
      .reduce((acc, s) => acc + (s.focusDurationMs || 0), 0);
  });

  let timeComparison = $derived.by(() => {
    if (todayTimeMs === 0 && yesterdayTimeMs === 0) return { pct: 0, up: true, newHigh: false, sinceLast: false };
    if (yesterdayTimeMs === 0) {
      const lastSession = sessions
        .filter(s => s.status === 'finished' && (s.focusDurationMs || 0) > 0 && new Date(s.startedAt).getTime() < new Date().setHours(0,0,0,0))
        .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())[0];

      if (lastSession) {
        const lastTimeMs = lastSession.focusDurationMs || 0;
        const diff = todayTimeMs - lastTimeMs;
        const pct = Math.round((diff / lastTimeMs) * 100);
        return { pct: Math.abs(pct), up: pct >= 0, newHigh: false, sinceLast: true };
      }
      return { pct: 100, up: true, newHigh: true, sinceLast: false };
    }
    const diff = todayTimeMs - yesterdayTimeMs;
    const pct = Math.round((diff / yesterdayTimeMs) * 100);
    return { pct: Math.abs(pct), up: pct >= 0, newHigh: false, sinceLast: false };
  });
  let todayTimeStr = $derived.by(() => {
    const total = Math.floor(todayTimeMs / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  });

  let activityLabels = $derived.by(() => {
    const days = activityRange === -1
      ? Math.max(1, Math.ceil((Date.now() - Math.min(...sessions.map(s => new Date(s.startedAt).getTime()), Date.now())) / 86400000))
      : activityRange;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    now.setDate(now.getDate() - activityOffset * days);
    const out: string[] = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      out.push(d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }));
    }
    return out;
  });

  let activityData = $derived.by(() => {
    const days = activityRange === -1
      ? Math.max(1, Math.ceil((Date.now() - Math.min(...sessions.map(s => new Date(s.startedAt).getTime()), Date.now())) / 86400000))
      : activityRange;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    now.setDate(now.getDate() - activityOffset * days);
    const out: number[] = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const k = dayKey(d);
      const totalMs = sessions
        .filter((s) => s.status === 'finished' && dayKey(new Date(s.startedAt)) === k)
        .reduce((acc, s) => acc + (s.focusDurationMs || 0), 0);
      out.push(Math.round(totalMs / 60000));
    }
    return out;
  });

  let activitySessionsData = $derived.by(() => {
    const days = activityRange === -1
      ? Math.max(1, Math.ceil((Date.now() - Math.min(...sessions.map(s => new Date(s.startedAt).getTime()), Date.now())) / 86400000))
      : activityRange;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    now.setDate(now.getDate() - activityOffset * days);
    const out: number[] = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const k = dayKey(d);
      const count = sessions.filter((s) => s.status === 'finished' && dayKey(new Date(s.startedAt)) === k).length;
      out.push(count);
    }
    return out;
  });

  let activityAvg = $derived(activityData.length === 0 ? 0 : activityData.reduce((a, b) => a + b, 0) / activityData.length);
  let activityAvgLine = $derived(activityData.map(() => activityAvg));

  let todayAttempts = $derived.by(() => {
    const todayKey = dayKey(new Date());
    const todaySessionIds = new Set(
      sessions
        .filter((s) => dayKey(new Date(s.startedAt)) === todayKey)
        .map((s) => s.id)
    );
    return attempts.filter((a) => todaySessionIds.has(a.macroSessionId));
  });
  let todayReviews = $derived.by(() => {
    const todayKey = dayKey(new Date());
    return reviewEvents.filter((e) => dayKey(new Date(e.reviewedAt)) === todayKey);
  });
  let todayCorrectCount = $derived(todayAttempts.filter((a) => a.result === 'correct').length);
  let todayIncorrectCount = $derived(todayAttempts.filter((a) => a.result === 'incorrect').length);
  let todayUnresolvedCount = $derived(todayAttempts.filter((a) => a.result === 'unresolved').length);
  let todayRecalledCount = $derived(todayReviews.filter((e) => e.rating === 3).length);
  let todayTotalCount = $derived(todayAttempts.length + todayReviews.length);

  let globalRecalledCount = $derived(reviewEvents.filter((e) => e.rating === 3).length);
  let globalTotalCount = $derived(attempts.length + globalRecalledCount);

  let todayPieData = $derived([todayCorrectCount, todayIncorrectCount, todayRecalledCount, todayUnresolvedCount]);
  let globalPieData = $derived([correctCount, incorrectCount, globalRecalledCount, unresolvedCount]);

  function pctOf(n: number, total: number): number {
    return total === 0 ? 0 : Math.round((n / total) * 100);
  }
  function formatCount(n: number): string {
    return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
  }
  let dailyAverageMinutes = $derived.by(() => {
    const byDay = new Map<string, number>();
    sessions
      .filter((s) => s.status === 'finished')
      .forEach((s) => {
        const k = dayKey(new Date(s.startedAt));
        byDay.set(k, (byDay.get(k) ?? 0) + (s.focusDurationMs || 0) / 60000);
      });
    if (byDay.size === 0) return 0;
    return Math.round([...byDay.values()].reduce((a, b) => a + b, 0) / byDay.size);
  });
  let longestStreak = $derived.by(() => {
    const days = new Set(
      sessions
        .filter((s) => s.status === 'finished')
        .map((s) => dayKey(new Date(s.startedAt)))
    );
    if (days.size === 0) return 0;
    const sorted = [...days].sort();
    let max = 1;
    let cur = 1;
    for (let i = 1; i < sorted.length; i++) {
      const prev = new Date(sorted[i - 1]! + 'T00:00:00');
      const today = new Date(sorted[i]! + 'T00:00:00');
      const diff = Math.round((today.getTime() - prev.getTime()) / 86400000);
      if (diff === 1) {
        cur++;
        if (cur > max) max = cur;
      } else {
        cur = 1;
      }
    }
    return max;
  });
  let streakDays = $derived.by(() => {
    const days = new Set(
      sessions
        .filter((s) => s.status === 'finished')
        .map((s) => {
          const d = new Date(s.startedAt);
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        })
    );
    if (days.size === 0) return 0;
    let count = 0;
    const cursor = new Date();
    cursor.setHours(0, 0, 0, 0);
    while (true) {
      const k = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')}`;
      if (!days.has(k)) break;
      count++;
      cursor.setDate(cursor.getDate() - 1);
    }
    return count;
  });

  async function refresh() {
    try {
      items = await listAllRecallItems();
    } catch (err) {
      console.error('[Analytics] listAllRecallItems failed:', err);
    }
    try {
      const page = await listMacroSessionsPage({ limit: 5000 });
      sessions = page.sessions;
    } catch (err) {
      console.error('[Analytics] listMacroSessionsPage failed:', err);
    }
    try {
      attempts = await listAttemptsByUser();
    } catch (err) {
      console.error('[Analytics] listAttemptsByUser failed:', err);
      attempts = [];
    }
    try {
      reviewEvents = await listReviewEventsByUser();
    } catch (err) {
      console.error('[Analytics] listReviewEventsByUser failed:', err);
      reviewEvents = [];
    }
    try {
      const hm = buildYearHeatmap(sessions, new Date(), 53);
      weeks = hm.weeks;
    } catch (err) {
      console.error('[Analytics] buildYearHeatmap failed:', err);
    }

  }

  onMount(() => {
    void refresh();
  });

  $effect(() => {
    const canvas = thirtyDayCanvas;
    if (!canvas || Chart.getChart(canvas)) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const brandColor = getThemeColor('--color-brand', '#c9a96e');
    const gradient = ctx.createLinearGradient(0, 0, 0, 150);
    gradient.addColorStop(0, hexToRgba(brandColor, 1));   // Top: sólido
    gradient.addColorStop(0.4, hexToRgba(brandColor, 0.6)); // Medio: empieza a desvanecerse
    gradient.addColorStop(1, hexToRgba(brandColor, 0.15));  // Bottom: casi transparente, pero visible
    thirtyDayChart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: [],
        datasets: [
          {
            type: 'bar',
            data: [],
            backgroundColor: gradient,
            borderRadius: 2,
            borderSkipped: false,
            barPercentage: 0.7,
            order: 2
          },
          {
            type: 'line',
            data: [],
            borderColor: hexToRgba(getThemeColor('--color-text-primary', '#ffffff'), 0.4),
            borderWidth: 1.5,
            borderDash: [4, 4],
            pointRadius: 0,
            pointHoverRadius: 4,
            tension: 0.3,
            order: 1
          }
        ]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(18, 18, 21, 0.95)',
            titleColor: '#fff',
            bodyColor: '#d1d5db',
            borderColor: '#27272a',
            borderWidth: 1,
            padding: 10,
            callbacks: {
              label: (context) => {
                if (context.datasetIndex === 0) {
                  const mins = Number(context.parsed.y) || 0;
                  const hrs = (mins / 60).toFixed(1);
                  const sess = activitySessionsData[context.dataIndex] ?? 0;
                  return [`${hrs}h studied`, `${sess} sessions`];
                }
                return '';
              }
            }
          }
        },
        scales: {
          x: {
            display: true,
            grid: { display: false },
            border: { display: false },
            ticks: { font: { size: 9 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 7 }
          },
          y: {
            display: true,
            beginAtZero: true,
            grid: { color: 'rgba(128,128,128,0.12)', drawTicks: false },
            border: { display: false },
            ticks: { font: { size: 9 }, precision: 0, padding: 4 }
          }
        }
      }
    });
    return () => {
      try { thirtyDayChart?.destroy(); } catch { /* already destroyed */ }
      thirtyDayChart = null;
    };
  });

  $effect(() => {
    const chart = thirtyDayChart;
    if (!chart) return;
    const ds = chart.data.datasets;
    if (!ds[0] || !ds[1]) return;
    chart.data.labels = activityLabels;
    ds[0].data = activityData;
    ds[1].data = activityAvgLine;
    chart.update('none');
  });

  $effect(() => {
    const canvas = fsrsCanvas;
    if (!canvas || Chart.getChart(canvas)) return;
    const labelsDays = curves[0]!.points.map((p) => `${Math.round(p.t)}d`);
    fsrsChart = new Chart(canvas, {
      type: 'line',
      data: {
        labels: labelsDays,
        datasets: curves.map((c, i) => ({
          label: c.label,
          data: c.points.map((p) => Math.round(p.r * 100)),
          borderColor: FSRS_PALETTE[i % FSRS_PALETTE.length],
          borderWidth: 1.5,
          tension: 0.4,
          pointRadius: 0
        }))
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { display: false }, ticks: { font: { size: 8 } } },
          y: { 
            min: 0, 
            max: 100, 
            ticks: { callback: (v) => v + '%', font: { size: 8 } },
            grid: { color: 'rgba(128, 128, 128, 0.1)', drawTicks: false },
            border: { display: false }
          }
        }
      }
    });
    return () => {
      try { fsrsChart?.destroy(); } catch { /* already destroyed */ }
      fsrsChart = null;
    };
  });

  $effect(() => {
    const canvas = todayPieCanvas;
    if (!canvas || Chart.getChart(canvas)) return;
    todayPieChart = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels: ['Correct', 'Incorrect', 'Recalled', 'Unresolved'],
        datasets: [{
          data: [0, 0, 0, 0],
          backgroundColor: ['#10b981', '#ef4444', '#8b5cf6', '#3b82f6'],
          borderColor: getThemeColor('--color-surface', '#ffffff'),
          borderWidth: 4,
          hoverOffset: 6
        }]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        cutout: '75%',
        plugins: { legend: { display: false } }
      }
    });
    return () => {
      try { todayPieChart?.destroy(); } catch { /* already destroyed */ }
      todayPieChart = null;
    };
  });

  $effect(() => {
    const chart = todayPieChart;
    if (!chart) return;
    if (todayPieData.every(v => v === 0)) {
      chart.data.datasets[0]!.data = [1];
      chart.data.datasets[0]!.backgroundColor = [getThemeColor('--color-surface-sunken', '#eaeaea')];
    } else {
      chart.data.datasets[0]!.data = todayPieData;
      chart.data.datasets[0]!.backgroundColor = ['#10b981', '#ef4444', '#8b5cf6', '#3b82f6'];
    }
    chart.update('none');
  });

  $effect(() => {
    const canvas = globalPieCanvas;
    if (!canvas || Chart.getChart(canvas)) return;
    globalPieChart = new Chart(canvas, {
      type: 'doughnut',
      data: {
        labels: ['Correct', 'Incorrect', 'Recalled', 'Unresolved'],
        datasets: [{
          data: [0, 0, 0, 0],
          backgroundColor: ['#10b981', '#ef4444', '#8b5cf6', '#3b82f6'],
          borderColor: getThemeColor('--color-surface', '#ffffff'),
          borderWidth: 4,
          hoverOffset: 6
        }]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        cutout: '75%',
        plugins: { legend: { display: false } }
      }
    });
    return () => {
      try { globalPieChart?.destroy(); } catch { /* already destroyed */ }
      globalPieChart = null;
    };
  });

  $effect(() => {
    const chart = globalPieChart;
    if (!chart) return;
    if (globalPieData.every(v => v === 0)) {
      chart.data.datasets[0]!.data = [1];
      chart.data.datasets[0]!.backgroundColor = [getThemeColor('--color-surface-sunken', '#eaeaea')];
    } else {
      chart.data.datasets[0]!.data = globalPieData;
      chart.data.datasets[0]!.backgroundColor = ['#10b981', '#ef4444', '#8b5cf6', '#3b82f6'];
    }
    chart.update('none');
  });

  $effect(() => {
    const canvas = perfCanvas;
    if (!canvas || Chart.getChart(canvas)) return;

    perfChart = new Chart(canvas, {
      data: {
        labels: [],
        datasets: [
          {
            type: 'bar', label: 'Volume', data: [],
            backgroundColor: hexToRgba(getThemeColor('--color-brand', '#c9a96e'), 0.25),
            borderColor: hexToRgba(getThemeColor('--color-brand', '#c9a96e'), 0.5),
            borderWidth: 1, borderRadius: 2, yAxisID: 'y', order: 4
          },
          { type: 'line', label: 'Avg', data: [], borderColor: getThemeColor('--color-text-muted', '#9ca3af'), borderWidth: 1.5, borderDash: [5, 5], tension: 0.4, pointRadius: 0, yAxisID: 'y1', order: 3 },
          { type: 'line', label: 'P50', data: [], borderColor: getThemeColor('--color-success', '#10b981'), borderWidth: 1.5, tension: 0.4, pointRadius: 0, yAxisID: 'y1', order: 2 },
          { type: 'line', label: 'P85', data: [], borderColor: getThemeColor('--color-warning', '#f59e0b'), borderWidth: 1.5, tension: 0.4, pointRadius: 0, yAxisID: 'y1', order: 1 },
          { type: 'line', label: 'P95', data: [], borderColor: getThemeColor('--color-danger', '#ef4444'), borderWidth: 1.5, tension: 0.4, pointRadius: 0, yAxisID: 'y1', order: 0 }
        ]
      },
      options: {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: { legend: { display: false }, tooltip: { displayColors: true } },
        scales: {
          x: { grid: { display: false }, ticks: { maxTicksLimit: 7, font: { size: 8 } } },
          y: {
            type: 'linear', display: true, position: 'left', beginAtZero: true,
            grid: { color: 'rgba(128,128,128,0.1)', drawTicks: false },
            border: { display: false }, ticks: { font: { size: 8 }, precision: 0 }
          },
          y1: {
            type: 'linear', display: true, position: 'right', beginAtZero: true,
            grid: { drawOnChartArea: false }, border: { display: false },
            ticks: { font: { size: 8 } }
          }
        }
      }
    });
    return () => {
      try { perfChart?.destroy(); } catch { /* already destroyed */ }
      perfChart = null;
    };
  });

  $effect(() => {
    const chart = perfChart;
    if (!chart) return;
    const ds = chart.data.datasets;
    if (!ds[0] || !ds[1] || !ds[2] || !ds[3] || !ds[4]) return;
    
    chart.data.labels = perfData.labels;
    ds[0].data = perfData.volumes;
    ds[1].data = perfData.avgs;
    ds[2].data = perfData.p50;
    ds[3].data = perfData.p85;
    ds[4].data = perfData.p95;
    chart.update('none');
  });

  let monthLabels = $derived.by(() => {
    const out = new Map<number, string>();
    let lastMonth = -1;
    let lastLabelIdx = -10;
    weeks.forEach((week, wi) => {
      const first = week.days[0];
      if (!first) return;
      const [, m] = first.date.split('-').map(Number);
      const month = m ?? 1;
      if (month !== lastMonth) {
        if (wi - lastLabelIdx >= 3 || wi === 0) {
          out.set(
            wi,
            new Date(2000, month - 1, 1)
              .toLocaleDateString(undefined, { month: 'short' })
              .replace('.', '')
          );
          lastLabelIdx = wi;
        }
        lastMonth = month;
      }
    });
    return out;
  });

  function getPercentile(arr: number[], p: number): number {
    if (arr.length === 0) return 0;
    const sorted = [...arr].sort((a, b) => a - b);
    const index = (p / 100) * (sorted.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    const weight = index % 1;
    const lVal = sorted[lower] ?? 0;
    const uVal = sorted[upper] ?? 0;
    return lVal * (1 - weight) + uVal * weight;
  }

  let perfData = $derived.by(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - perfRange);

    const filtered = attempts.filter((a) => {
      if (!a.createdAt) return false;
      const time = new Date(a.createdAt).getTime();
      return !isNaN(time) && time >= cutoff.getTime();
    });

    const groups: Record<string, number[]> = {};

    filtered.forEach((a) => {
      const k = dayKey(new Date(a.createdAt));
      if (!groups[k]) groups[k] = [];
      groups[k].push((a.durationMs || 0) / 60000); // minutes
    });

    const sortedKeys = Object.keys(groups).sort();
    const labels: string[] = [];
    const volumes: number[] = [];
    const avgs: number[] = [];
    const p50: number[] = [];
    const p85: number[] = [];
    const p95: number[] = [];

    sortedKeys.forEach((k) => {
      const vals = groups[k]!;
      labels.push(new Date(k + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      volumes.push(vals.length);
      avgs.push(vals.reduce((a, b) => a + b, 0) / vals.length);
      p50.push(getPercentile(vals, 50));
      p85.push(getPercentile(vals, 85));
      p95.push(getPercentile(vals, 95));
    });

    return { labels, volumes, avgs, p50, p85, p95 };
  });

  function heatColor(level: number): string {
    if (level === 0) return 'color-mix(in srgb, var(--color-text-primary) 10%, transparent)';
    if (level === 1) return 'color-mix(in srgb, var(--color-accent) 20%, transparent)';
    if (level === 2) return 'color-mix(in srgb, var(--color-accent) 45%, transparent)';
    if (level === 3) return 'color-mix(in srgb, var(--color-accent) 75%, transparent)';
    return 'var(--color-accent)';
  }

</script>

<section class="view">
  <header class="view-head">
    <div class="view-head-text">
      <h1 class="view-title">{t('analytics.title')}</h1>
      <p class="view-subtitle">{t('analytics.subtitle')}</p>
    </div>
  </header>

  {#if items.length === 0 && sessions.length === 0}
    <EmptyState
      icon="bar-chart-3"
      title={t('analytics.empty')}
      description={t('analytics.emptyHint')}
    />
  {:else}
    <div class="dashboard-grid">

      <!-- Activity (row 1, col 1) -->
      <div class="card chart-card activity-card">
        <div class="activity-header">
          <div class="activity-top">
            <h2 class="card-title">Activity</h2>
            <div class="activity-display">
              <span class="big-time">{todayTimeStr}</span>
              <span class="today-label">TODAY</span>
              {#if timeComparison.newHigh}
                <span class="comparison-badge comparison-badge--up">↑ 100%</span>
              {:else if timeComparison.pct > 0}
                <span class="comparison-badge {timeComparison.up ? 'comparison-badge--up' : 'comparison-badge--down'}">
                  {timeComparison.up ? '↑' : '↓'} {timeComparison.pct}%
                  {#if timeComparison.sinceLast}
                    <span class="since-label"> {t('analytics.kpi.sinceLastSession')}</span>
                  {/if}
                </span>
              {/if}
            </div>
          </div>
          <div class="activity-actions">
            <div class="chart-controls">
              <button type="button" class:active={activityRange === 1} onclick={() => activityRange = 1}>1d</button>
              <button type="button" class:active={activityRange === 7} onclick={() => activityRange = 7}>7d</button>
              <button type="button" class:active={activityRange === 30} onclick={() => activityRange = 30}>30d</button>
              <button type="button" class:active={activityRange === 90} onclick={() => activityRange = 90}>90d</button>
              <button type="button" class:active={activityRange === 365} onclick={() => activityRange = 365}>1y</button>
              <button type="button" class:active={activityRange === -1} onclick={() => activityRange = -1}>All</button>
            </div>
            <div class="period-nav">
              <button type="button" onclick={() => activityOffset++} aria-label="Previous period">←</button>
              <button type="button" onclick={() => activityOffset = Math.max(0, activityOffset - 1)} disabled={activityOffset === 0} aria-label="Next period">→</button>
            </div>
          </div>
        </div>
        <div class="chart-wrapper" style="margin-top: var(--space-2);">
          <canvas bind:this={thirtyDayCanvas} aria-label="Activity chart"></canvas>
        </div>
      </div>

      <!-- Global Breakdown (row 1, col 2) -->

      <div class="card chart-card global-card">
        <h2 class="card-title" style="margin-bottom: var(--space-2);">{t('analytics.breakdown.globalTitle')}</h2>
        <div class="global-breakdown-top">
          <span class="global-total">{formatCount(globalTotalCount)}</span>
          <span class="global-total-label">TOTAL</span>
        </div>
        <div class="global-bar">
          <div class="global-seg bg-success" style="width: {pctOf(correctCount, globalTotalCount)}%"></div>
          <div class="global-seg bg-danger" style="width: {pctOf(incorrectCount, globalTotalCount)}%"></div>
          <div class="global-seg bg-recall" style="width: {pctOf(globalRecalledCount, globalTotalCount)}%"></div>
          <div class="global-seg bg-info" style="width: {pctOf(unresolvedCount, globalTotalCount)}%"></div>
        </div>
        <div class="global-legend">
          <div class="legend-row">
            <span class="legend-dot legend-dot--success"></span>
            <span class="legend-label">{t('analytics.kpi.correct')}</span>
            <span class="legend-value">{correctCount} <span class="legend-pct">({pctOf(correctCount, globalTotalCount)}%)</span></span>
          </div>
          <div class="legend-row">
            <span class="legend-dot legend-dot--danger"></span>
            <span class="legend-label">{t('analytics.kpi.incorrect')}</span>
            <span class="legend-value">{incorrectCount} <span class="legend-pct">({pctOf(incorrectCount, globalTotalCount)}%)</span></span>
          </div>
          <div class="legend-row">
            <span class="legend-dot legend-dot--recall"></span>
            <span class="legend-label">{t('analytics.kpi.recalled')}</span>
            <span class="legend-value">{globalRecalledCount} <span class="legend-pct">({pctOf(globalRecalledCount, globalTotalCount)}%)</span></span>
          </div>
          <div class="legend-row">
            <span class="legend-dot legend-dot--info"></span>
            <span class="legend-label">{t('analytics.kpi.unresolved')}</span>
            <span class="legend-value">{unresolvedCount} <span class="legend-pct">({pctOf(unresolvedCount, globalTotalCount)}%)</span></span>
          </div>
        </div>
      </div>

      <!-- Today's Breakdown (row 2, col 1) -->
      <div class="card pie-card">
        <h2 class="card-title" style="margin-bottom: var(--space-3);">{t('analytics.breakdown.todayTitle')}</h2>
        <div class="today-layout">
          <div class="pie-container">
            <canvas bind:this={todayPieCanvas}></canvas>
            <div class="pie-center">
              <span class="pie-total">{formatCount(todayTotalCount)}</span>
              <span class="pie-total-label">{t('analytics.breakdown.total')}</span>
            </div>
          </div>
          <div class="pie-legend">
            <div class="legend-row">
              <span class="legend-dot legend-dot--success"></span>
              <span class="legend-label">{t('analytics.kpi.correct')}</span>
              <span class="legend-value">{todayCorrectCount} <span class="legend-pct">({pctOf(todayCorrectCount, todayTotalCount)}%)</span></span>
            </div>
            <div class="legend-row">
              <span class="legend-dot legend-dot--danger"></span>
              <span class="legend-label">{t('analytics.kpi.incorrect')}</span>
              <span class="legend-value">{todayIncorrectCount} <span class="legend-pct">({pctOf(todayIncorrectCount, todayTotalCount)}%)</span></span>
            </div>
            <div class="legend-row">
              <span class="legend-dot legend-dot--recall"></span>
              <span class="legend-label">{t('analytics.kpi.recalled')}</span>
              <span class="legend-value">{todayRecalledCount} <span class="legend-pct">({pctOf(todayRecalledCount, todayTotalCount)}%)</span></span>
            </div>
            <div class="legend-row">
              <span class="legend-dot legend-dot--info"></span>
              <span class="legend-label">{t('analytics.kpi.unresolved')}</span>
              <span class="legend-value">{todayUnresolvedCount} <span class="legend-pct">({pctOf(todayUnresolvedCount, todayTotalCount)}%)</span></span>
            </div>
          </div>
        </div>
      </div>

      <!-- Performance & Load (row 2, col 2) -->
      <div class="card chart-card perf-card">
        <div class="card-head">
          <h2 class="card-title">Performance & Load</h2>
          <div class="chart-controls">
            <button type="button" class:active={perfRange === 7} onclick={() => perfRange = 7}>7d</button>
            <button type="button" class:active={perfRange === 30} onclick={() => perfRange = 30}>30d</button>
            <button type="button" class:active={perfRange === 90} onclick={() => perfRange = 90}>90d</button>
            <button type="button" class:active={perfRange === 365} onclick={() => perfRange = 365}>1y</button>
          </div>
        </div>
        <div class="chart-wrapper">
          <canvas bind:this={perfCanvas} aria-label="Performance and Load"></canvas>
        </div>
      </div>

      <!-- FSRS (row 3, col 1) -->
      <div class="card chart-card fsrs-card">
        <div class="card-head">
          <h2 class="card-title">{t('analytics.curves.title')}</h2>
          <div class="help-tooltip-wrap">
            <button type="button" class="help-icon" aria-label={t('analytics.chart.legend')}>?</button>
            <div class="help-tooltip" role="tooltip">
              <div class="help-tooltip-title">{t('analytics.chart.legend')}</div>
              <div class="help-tooltip-legend">
                {#each curves as c, i (c.stability)}
                  <div class="help-tooltip-item">
                    <span class="help-tooltip-dot" style="background: {FSRS_PALETTE[i % FSRS_PALETTE.length]}"></span>
                    <span>{c.label}</span>
                  </div>
                {/each}
              </div>
              <p class="help-tooltip-hint">{t('analytics.curves.hint')}</p>
            </div>
          </div>
        </div>
        <div class="chart-wrapper">
          <canvas bind:this={fsrsCanvas} aria-label={t('analytics.chart.ariaLabel')}></canvas>
        </div>
      </div>

      <!-- Heatmap (row 3, cols 1-3) -->
      {#if weeks.length > 0}
        <div class="card heatmap-card">
          <div class="heatmap-header">
            <div class="heatmap-title-group">
              <h2 class="card-title">{t('analytics.heatmap.title')}</h2>
              {#if showRecentStartBanner}
                <div class="help-tooltip-wrap">
                  <button type="button" class="help-icon" aria-label="Info">i</button>
                  <div class="help-tooltip help-tooltip--left" role="tooltip">
                    <p class="help-tooltip-hint" style="padding-top: 0; border-top: none; white-space: normal;">
                      {t('analytics.heatmap.recentStartBanner', { days: String(daysLearned.days) })}
                    </p>
                  </div>
                </div>
              {/if}
            </div>
            <div class="heatmap-legend">
              <span class="hint">{t('analytics.heatmap.legendLess')}</span>
              <span class="legend-cell" style="background: {heatColor(0)}" title={t('analytics.heatmap.legendTooltip0')}></span>
              <span class="legend-cell" style="background: {heatColor(1)}" title={t('analytics.heatmap.legendTooltip1')}></span>
              <span class="legend-cell" style="background: {heatColor(2)}" title={t('analytics.heatmap.legendTooltip2')}></span>
              <span class="legend-cell" style="background: {heatColor(3)}" title={t('analytics.heatmap.legendTooltip3')}></span>
              <span class="legend-cell" style="background: {heatColor(4)}" title={t('analytics.heatmap.legendTooltip4')}></span>
              <span class="hint">{t('analytics.heatmap.legendMore')}</span>
            </div>
          </div>
          <div class="heatmap-body">
            <div class="heatmap-scroll">
              <div class="heatmap-layout">

                <div class="heatmap-main">
                  <div class="heatmap-months" aria-hidden="true">
                    {#each weeks as _w, wi (wi)}
                      <span class="heatmap-month-slot">
                        {#if monthLabels.has(wi)}{monthLabels.get(wi)}{/if}
                      </span>
                    {/each}
                  </div>
                  <div class="heatmap-grid">
                    {#each weeks as week, wi (wi)}
                      {#each week.days as day (day.date)}
                        <div
                          class="heatmap-day"
                          data-level={day.isFuture ? undefined : day.level}
                          data-future={day.isFuture || undefined}
                          role={day.isFuture ? undefined : 'img'}
                          aria-label={day.isFuture ? undefined : t('analytics.heatmap.dayDetail', { date: day.date, min: day.minutes })}
                          title={t('analytics.heatmap.dayDetail', { date: day.date, min: day.minutes })}
                        ></div>
                      {/each}
                    {/each}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="heatmap-stats-footer">
            <div class="heatmap-sidebar-stat">
              <p class="heatmap-sidebar-label">{t('analytics.kpi.dailyAvg')}</p>
              <p class="heatmap-sidebar-value">{dailyAverageMinutes} min</p>
            </div>
            <div class="heatmap-sidebar-stat">
              <p class="heatmap-sidebar-label">{t('analytics.kpi.daysLearned')}</p>
              <p class="heatmap-sidebar-value">{daysLearned.days} <span class="heatmap-sidebar-muted">({daysLearned.pct}%)</span></p>
            </div>
            <div class="heatmap-sidebar-stat">
              <p class="heatmap-sidebar-label">{t('analytics.kpi.longestStreak')}</p>
              <p class="heatmap-sidebar-value">{longestStreak} <span class="heatmap-sidebar-muted">{longestStreak === 1 ? t('analytics.kpi.day') : t('analytics.kpi.days')}</span></p>
            </div>
            <div class="heatmap-sidebar-stat">
              <p class="heatmap-sidebar-label">{t('analytics.kpi.streak')}</p>
              <p class="heatmap-sidebar-value">{streakDays}d</p>
            </div>
          </div>
        </div>
      {/if}

    </div>
  {/if}
</section>

<style>
  .view {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    width: 100%;
    max-width: 100%;
    height: 100%;
    min-height: 0;
    overflow: hidden;
  }

  .view-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--space-3);
    flex-shrink: 0;
  }

  .view-head-text {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .view-subtitle {
    margin: 0;
    font-family: var(--font-body, var(--font-sans));
    font-size: var(--text-sm);
    color: var(--color-text-muted);
  }

  .view-title {
    margin: 0;
    font-family: var(--font-display);
    font-size: 1.35rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.1;
    color: var(--color-text-primary);
    text-wrap: balance;
  }

  .hint {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--color-text-muted);
  }

  /* ============================================================
     DASHBOARD — réplica del .metric-dashboard del vanilla
     ============================================================ */
  .heatmap-card,
  .chart-card {
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-md);
  }

  .chart-card {
    padding: var(--space-6);
  }

  .chart-wrapper {
    position: relative;
    width: 100%;
    max-width: 100%;
    flex: 1;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
  }

  .chart-wrapper canvas {
    max-width: 100% !important;
    max-height: 100% !important;
    display: block;
  }

  .heatmap-card {
    padding: var(--space-5);
    background: var(--color-surface-sunken);
  }

  .heatmap-card,
  .chart-card {
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
  }

  .heatmap-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-4);
    margin-bottom: var(--space-4);
    flex-wrap: wrap;
  }

  .heatmap-title-group {
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }

  .help-tooltip--left {
    right: auto;
    left: 0;
  }

  .heatmap-legend {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: var(--text-caption);
    color: var(--color-text-muted);
  }

  .heatmap-scroll {
    display: flex;
    justify-content: stretch;
    flex: 1;
    min-width: 0;
    overflow-x: auto;
    padding: var(--space-1) 0 var(--space-3) 0;
  }

  .heatmap-layout {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .heatmap-main {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
  }

  .heatmap-grid {
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: repeat(53, minmax(6px, 1fr));
    grid-template-rows: repeat(7, auto);
    gap: 5px;
  }

  .heatmap-day {
    width: 100%;
    height: auto;
    aspect-ratio: 1;
    max-width: 22px;
    max-height: 22px;
    margin: 0 auto;
    min-width: 0;
    border-radius: 3px;
    background: color-mix(in srgb, var(--color-text-primary) 10%, transparent);
    box-shadow: inset 0 0 0 1px var(--heat-border);
    outline: 1px solid transparent;
    outline-offset: 1px;
    transition: outline-color 100ms ease-out;
    cursor: pointer;
  }

  .heatmap-day:hover {
    outline-color: var(--heat-hover-ring);
  }

  .heatmap-day[data-future] {
    background: transparent;
    cursor: default;
  }

  .heatmap-day[data-future]:hover {
    transform: none;
    outline-color: transparent;
  }

  .heatmap-day[data-level='0'] {
    background: color-mix(in srgb, var(--color-text-primary) 10%, transparent);
  }
  .heatmap-day[data-level='1'] { background: color-mix(in srgb, var(--color-accent) 20%, transparent); }
  .heatmap-day[data-level='2'] { background: color-mix(in srgb, var(--color-accent) 45%, transparent); }
  .heatmap-day[data-level='3'] { background: color-mix(in srgb, var(--color-accent) 75%, transparent); }
  .heatmap-day[data-level='4'] { background: var(--color-accent); }

  .heatmap-months {
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: repeat(53, minmax(6px, 1fr));
    gap: 5px;
    height: 16px;
    font-family: var(--font-body, var(--font-sans));
    font-size: 10px;
    color: var(--color-text-secondary);
    font-weight: 500;
    white-space: nowrap;
    overflow: visible;
  }

  .heatmap-month-slot {
    position: relative;
    display: block;
    height: 12px;
    text-align: center;
  }

  .legend-cell {
    width: 11px;
    height: 11px;
    border-radius: 2px;
    box-shadow: inset 0 0 0 1px var(--heat-border);
  }

  .dashboard-grid {
    display: grid;
    grid-template-columns: repeat(10, 1fr);
    grid-template-rows: repeat(3, minmax(0, 1fr));
    gap: var(--space-3);
    width: 100%;
    flex: 1;
    min-height: 0;
  }

  .dashboard-grid > * {
    min-width: 0;
    min-height: 0;
  }

  .pie-card {
    background: var(--color-surface);
    border: var(--border-width) solid var(--color-border);
    border-radius: var(--radius-md);
  }

  .activity-card {
    grid-column: span 7;
    grid-row: 1;
    padding: var(--space-4);
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .global-card {
    grid-column: span 3;
    grid-row: 1;
    padding: var(--space-4);
    display: flex;
    flex-direction: column;
    min-height: 0;
  }



  .perf-card {
    grid-column: span 6;
    grid-row: 2;
    padding: var(--space-4);
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .fsrs-card {
    grid-column: span 3;
    grid-row: 3;
    padding: var(--space-4);
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .pie-card {
    grid-column: span 4;
    grid-row: 2;
    padding: var(--space-4);
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .card-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--space-1);
    flex-shrink: 0;
  }

  .chart-controls {
    display: flex;
    gap: var(--space-1);
  }

  .chart-controls button {
    background: transparent;
    border: 1px solid var(--color-border);
    color: var(--color-text-muted);
    border-radius: var(--radius-sm);
    padding: 0 6px;
    height: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 0.65rem;
    font-weight: 600;
    transition: all var(--dur-fast) var(--ease-out);
  }

  .chart-controls button.active {
    background: var(--color-accent);
    color: var(--color-accent-fg);
    border-color: var(--color-accent);
  }

  .activity-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: var(--space-2);
    margin-bottom: var(--space-2);
  }

  .activity-top {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .activity-actions {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--space-2);
  }

  .activity-display {
    display: flex;
    align-items: baseline;
    gap: var(--space-2);
  }

  .since-label {
    font-size: 0.65rem;
    font-weight: 500;
    text-transform: lowercase;
  }

  .today-label {
    font-size: 0.6rem;
    font-weight: 700;
    color: var(--color-text-muted);
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .period-nav {
    display: flex;
    gap: var(--space-1);
  }

  .period-nav button {
    background: transparent;
    border: 1px solid var(--color-border);
    color: var(--color-text-muted);
    border-radius: var(--radius-sm);
    width: 22px;
    height: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 0.7rem;
    transition: all var(--dur-fast) var(--ease-out);
  }

  .period-nav button:hover:not(:disabled) {
    background: var(--color-surface-sunken);
    color: var(--color-text-primary);
    border-color: var(--color-text-primary);
  }

  .period-nav button:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .big-time {
    font-family: var(--font-mono);
    font-size: 2.25rem;
    font-weight: 700;
    color: var(--color-text-primary);
    line-height: 1;
    letter-spacing: -0.03em;
  }

  .comparison-badge {
    font-size: 0.75rem;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: var(--radius-sm);
    display: inline-flex;
    align-items: center;
    background: var(--color-surface-sunken);
  }

  .comparison-badge--up { color: var(--color-success); }
  .comparison-badge--down { color: var(--color-danger); }

  .global-breakdown-top {
    display: flex;
    align-items: baseline;
    gap: var(--space-2);
    margin-bottom: var(--space-3);
  }

  .global-total {
    font-family: var(--font-mono);
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--color-text-primary);
    line-height: 1;
    letter-spacing: -0.03em;
  }

  .global-total-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .global-bar {
    display: flex;
    width: 100%;
    height: 12px;
    border-radius: var(--radius-full);
    overflow: hidden;
    background: var(--color-surface-sunken);
    margin-bottom: var(--space-4);
  }

  .global-seg {
    height: 100%;
  }
  .global-seg.bg-success { background: #10b981; }
  .global-seg.bg-danger { background: #ef4444; }
  .global-seg.bg-recall { background: #8b5cf6; }
  .global-seg.bg-info { background: #3b82f6; }

  .global-legend {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    font-size: 0.75rem;
  }

  .today-layout {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: var(--space-6);
    flex: 1;
  }

  .today-layout .pie-container {
    margin: 0;
    width: 180px;
    height: 180px;
    flex-shrink: 0;
  }

  .today-layout .pie-legend {
    flex: 1;
    max-width: 220px;
  }

  .chart-controls button:hover:not(:disabled) {
    background: var(--color-surface-sunken);
    color: var(--color-text-primary);
    border-color: var(--color-text-primary);
  }

  .chart-controls button:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .help-tooltip-wrap {
    position: relative;
    display: inline-flex;
    align-items: center;
  }

  .help-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: var(--radius-full);
    border: 1px solid var(--color-border);
    background: transparent;
    color: var(--color-text-muted);
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    cursor: help;
    transition: all var(--dur-fast) var(--ease-out);
    font-family: var(--font-sans);
    padding: 0;
  }

  .help-icon:hover,
  .help-icon:focus-visible {
    color: var(--color-text-primary);
    border-color: var(--color-text-primary);
    outline: none;
  }

  .help-tooltip {
    position: absolute;
    top: calc(100% + 8px);
    right: 0;
    z-index: var(--z-dropdown);
    width: 280px;
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    padding: var(--space-3);
    box-shadow: var(--shadow-md);
    opacity: 0;
    visibility: hidden;
    transform: translateY(-4px);
    transition: opacity 150ms var(--ease-out), transform 150ms var(--ease-out), visibility 150ms;
    pointer-events: none;
    text-align: left;
  }

  .help-tooltip-wrap:hover .help-tooltip,
  .help-tooltip-wrap:focus-within .help-tooltip {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
    pointer-events: auto;
  }

  .help-tooltip-title {
    font-size: 0.625rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--color-text-muted);
    margin-bottom: var(--space-2);
  }

  .help-tooltip-legend {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-1) var(--space-3);
    margin-bottom: var(--space-2);
  }

  .help-tooltip-item {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    color: var(--color-text-primary);
  }

  .help-tooltip-dot {
    width: 10px;
    height: 3px;
    border-radius: 2px;
    display: inline-block;
  }

  .help-tooltip-hint {
    margin: 0;
    font-size: 0.6875rem;
    line-height: 1.5;
    color: var(--color-text-muted);
    padding-top: var(--space-2);
    border-top: 1px solid var(--color-border);
  }



  .card-title {
    margin: 0;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--color-text-primary);
    line-height: 1.2;
  }

  .heatmap-card {
    grid-column: span 7;
    grid-row: 3;
    padding: var(--space-3);
    background: var(--color-surface);
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .heatmap-body {
    flex: 1;
    display: flex;
    gap: var(--space-3);
    align-items: center;
    min-height: 0;
    margin-top: var(--space-2);
    overflow: hidden;
  }

  .heatmap-stats-footer {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-around;
    align-items: center;
    margin-top: var(--space-4);
    padding-top: var(--space-3);
    border-top: var(--border-width) solid var(--color-border);
  }

  .heatmap-sidebar-stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1;
    min-width: 0;
    gap: 2px;
    text-align: center;
  }

  .heatmap-sidebar-label {
    margin: 0;
    font-size: 0.625rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-muted);
    white-space: nowrap;
  }

  .heatmap-sidebar-value {
    margin: 0;
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--color-text-primary);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .heatmap-sidebar-muted {
    font-size: 0.6875rem;
    color: var(--color-text-muted);
    font-weight: 400;
  }

  .pie-container {
    position: relative;
    width: 100px;
    height: 100px;
    margin: 0 auto var(--space-2) auto;
    flex-shrink: 0;
  }

  .pie-center {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  .pie-total {
    font-family: var(--font-mono);
    font-weight: 700;
    font-size: 1.25rem;
    color: var(--color-text-primary);
    line-height: 1;
  }

  .pie-total-label {
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--color-text-muted);
    line-height: 1;
  }

  .pie-legend {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    font-size: 0.6875rem;
  }

  .legend-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-2);
    color: var(--color-text-muted);
  }

  .legend-dot {
    width: 6px;
    height: 6px;
    border-radius: var(--radius-full);
    flex-shrink: 0;
    display: inline-block;
  }

  .legend-dot--success { background: #10b981; }
  .legend-dot--danger { background: #ef4444; }
  .legend-dot--recall { background: #8b5cf6; }
  .legend-dot--info { background: #3b82f6; }

  .legend-label {
    flex: 1;
    min-width: 0;
    text-align: left;
  }

  .legend-value {
    font-family: var(--font-mono);
    color: var(--color-text-primary);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .legend-pct {
    color: var(--color-text-muted);
  }

  @media (max-width: 1200px) {
    .dashboard-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    .activity-card, .global-card, .pie-card, .perf-card, .fsrs-card, .heatmap-card {
      grid-column: span 1;
      grid-row: auto;
    }
    .heatmap-body { flex-direction: column; gap: var(--space-3); }
    .heatmap-stats-footer {
      flex-wrap: wrap;
      gap: var(--space-3);
    }
  }

  @media (max-width: 700px) {
    .dashboard-grid {
      grid-template-columns: 1fr;
    }
    .activity-card, .global-card, .pie-card, .perf-card, .fsrs-card, .heatmap-card {
      grid-column: 1;
      grid-row: auto;
    }
    .today-layout {
      flex-direction: column;
    }
  }
</style>